import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { parseLiveBytes } from './liveBytesParser'

/**
 * LivePressReleaseButtonControl — PM-3 (Start/Stop Long-Press)
 *
 * Press  → sends Byte A (pressBytes) once.
 * Release → sends Byte B (releaseBytes) once.
 * No continuous spamming; no acknowledgement gate.
 *
 * Useful for driving device-side "run until told to stop" actions where the
 * device executes continuously between the two explicit signals.
 *
 * Props:
 *   comp           – full component object from play data
 *   outputStates   – current output state map
 *   onChange       – (mapping, value) callback
 *   resolveImage   – image resolver function
 *   highlightClass – CSS class string for step highlighting
 *   style          – positioning style object
 */
function LivePressReleaseButtonControl({
    comp,
    outputStates,
    onChange,
    resolveImage,
    highlightClass,
    style,
    onRequestActivate,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    const isPressedRef = useRef(false)
    const cleanupRefs = useRef({})

    const pressStr = comp.properties.pressBytes || ''
    const releaseStr = comp.properties.releaseBytes || ''
    const pressBytesArray = useMemo(() => parseLiveBytes(pressStr), [pressStr])
    const releaseBytesArray = useMemo(() => parseLiveBytes(releaseStr), [releaseStr])

    const sendBytes = useCallback(
        (arr) => {
            if (!arr.length || !worker || !webSerial?.isConnected) return
            worker.postMessage({ type: 'writeArray', value: arr })
        },
        [worker, webSerial]
    )

    cleanupRefs.current.onChange = onChange
    cleanupRefs.current.sendBytes = sendBytes
    cleanupRefs.current.releaseBytesArray = releaseBytesArray
    cleanupRefs.current.mapping = comp.mapping

    // Unmount safety: send release bytes and reset state if the button is
    // still physically held when the component unmounts (e.g. step advance).
    useEffect(() => {
        return () => {
            if (isPressedRef.current) {
                cleanupRefs.current.sendBytes(cleanupRefs.current.releaseBytesArray)
                cleanupRefs.current.onChange(cleanupRefs.current.mapping, 0)
            }
        }
    }, [])

    const handlePointerDown = useCallback(
        (e) => {
            e.currentTarget.setPointerCapture(e.pointerId)
            isPressedRef.current = true
            onChange(comp.mapping, 1)
            // Only send bytes and proceed if PM-4 lock allows it.
            // sendBytes itself guards against no connection.
            if (!onRequestActivate || onRequestActivate()) {
                sendBytes(pressBytesArray)
            }
        },
        [comp.mapping, onChange, sendBytes, pressBytesArray, onRequestActivate]
    )

    const handlePointerUp = useCallback(() => {
        if (!isPressedRef.current) return
        isPressedRef.current = false
        onChange(comp.mapping, 0)
        sendBytes(releaseBytesArray)
    }, [comp.mapping, onChange, sendBytes, releaseBytesArray])

    const isBtnOn = (outputStates[comp.mapping] || 0) !== 0
    // Admin names images "Default Image" and "Active Image" per PM-3 spec.
    // Fall back to onImage/offImage for backward compatibility.
    const btnImg = isBtnOn
        ? comp.properties.activeImage || comp.properties.onImage || comp.properties.imageUrl
        : comp.properties.defaultImage || comp.properties.offImage || comp.properties.imageUrl

    return (
        <div
            id={comp.id}
            className={`absolute-component playscreen-action-button${highlightClass}`}
            style={{ ...style, zIndex: style?.zIndex ?? 20 }}
        >
            <img
                className={comp.properties.className || ''}
                src={resolveImage(btnImg)}
                alt={comp.name}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onDragStart={(e) => e.preventDefault()}
                style={{ cursor: 'pointer', touchAction: 'none' }}
            />
            <span className="control-label">
                {comp.properties.label || comp.name}
            </span>
        </div>
    )
}

export default LivePressReleaseButtonControl
